import { useState, useEffect, useCallback } from "react";
import {
  Box, Grid, Card, CardContent, Typography, TextField, Autocomplete,
  Button, CircularProgress, Collapse, Divider, Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BarChartIcon from "@mui/icons-material/BarChart";
import FolderIcon from "@mui/icons-material/Folder";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import InventoryIcon from "@mui/icons-material/Inventory";
import SearchIcon from "@mui/icons-material/Search";
import { getEstadisticasDigitalizaciones } from "../../helpers/CajasData";
import { getCuentasLocalizacionesAll } from "src/projects/gestion_comercial/helpers/CuentasLocalizaciones";
import esArray from "src/general_components/functionEsArray";
import datos from "src/services/datos";
import { CustomPagination } from "src/general_components/ListTablePaginated";
import { StyledDataGrid, estiloGrilla } from "src/general_components/FunctionsListTablePaginated";

const ROWS_PER_PAGE_OPTIONS = [50, 100, 150, 200];

const SummaryCard = ({ title, value, icon, color }) => (
  <Card sx={{ flex: 1, minWidth: 180 }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ color, fontSize: 40, display: "flex" }}>{icon}</Box>
      <Box>
        <Typography variant="h4" fontWeight="bold" color={color}>
          {value.toLocaleString()}
        </Typography>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
      </Box>
    </CardContent>
  </Card>
);

export const EstadisticasDigitalizaciones = () => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultados, setResultados] = useState(null);

  // Filtros
  const [idCaja, setIdCaja] = useState("");
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [sectorSeleccionado, setSectorSeleccionado] = useState(null);
  const [depositoSeleccionado, setDepositoSeleccionado] = useState(null);

  // Opciones
  const [clientes, setClientes] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [loadingSectores, setLoadingSectores] = useState(false);

  // Paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filtersActivos, setFiltersActivos] = useState(null);

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  // Cargar clientes desde localStorage
  useEffect(() => {
    const raw = localStorage.getItem("siam-cuentas_personas_juridicas");
    if (raw) {
      const parsed = JSON.parse(raw);
      setClientes(esArray(parsed?.cuentas_personas_juridicas || parsed || []));
    }
  }, []);

  // Cargar depósitos (owner_localizaciones)
  useEffect(() => {
    const fetchDepositos = async () => {
      try {
        const dep = new datos("owner_localizaciones");
        dep.addCondition("owner_localizaciones", "id", 0, ">");
        const res = await dep.get();
        setDepositos(esArray(res?.wsResponse?.owner_localizaciones || []));
      } catch (e) { /* silently fail */ }
    };
    fetchDepositos();
  }, []);

  // Cargar sectores al cambiar cliente
  useEffect(() => {
    const fetchSectores = async () => {
      setSectorSeleccionado(null);
      if (!clienteSeleccionado) {
        const all = await getCuentasLocalizacionesAll();
        setSectores(esArray(all));
        return;
      }
      setLoadingSectores(true);
      try {
        const { getCuentasLocalizaciones } = await import("src/projects/gestion_comercial/helpers/CuentasLocalizaciones");
        const res = await getCuentasLocalizaciones(clienteSeleccionado.id_cuentas);
        setSectores(esArray(res));
      } catch (e) {
        setSectores([]);
      } finally {
        setLoadingSectores(false);
      }
    };
    fetchSectores();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteSeleccionado]);

  const fetchData = useCallback(async (filters, pg, ps) => {
    setLoading(true);
    const offset = (pg - 1) * ps;
    const res = await getEstadisticasDigitalizaciones(filters, ps, offset);
    if (res) {
      setResultados(res);
      setTotalRecords(res.total_records);
    } else {
      setResultados(null);
      setTotalRecords(0);
    }
    setLoading(false);
  }, []);

  const handleBuscar = async () => {
    const filters = {};
    if (idCaja)               filters.id_caja = idCaja;
    if (clienteSeleccionado)  filters.id_cuentas = clienteSeleccionado.id_cuentas;
    if (sectorSeleccionado)   filters.id_cuentas_localizaciones = sectorSeleccionado.id;
    if (depositoSeleccionado) filters.id_deposito = depositoSeleccionado.id;

    setFiltersActivos(filters);
    setPage(1);
    fetchData(filters, 1, pageSize);
  };

  const handleLimpiar = () => {
    setIdCaja("");
    setClienteSeleccionado(null);
    setSectorSeleccionado(null);
    setDepositoSeleccionado(null);
    setResultados(null);
    setFiltersActivos(null);
    setPage(1);
    setTotalRecords(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(filtersActivos || {}, newPage, pageSize);
  };

  const handleChangeLimit = (e) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setPage(1);
    fetchData(filtersActivos || {}, 1, newSize);
  };

  const handleFirstPageClick = () => {
    setPage(1);
    fetchData(filtersActivos || {}, 1, pageSize);
  };

  const columns = [
    { field: "id_caja", headerName: "ID Caja", width: 90, headerAlign: "center", align: "center" },
    { field: "referencia", headerName: "Referencia", flex: 1, headerAlign: "left" },
    { field: "cliente", headerName: "Cliente", flex: 2, headerAlign: "left" },
    { field: "sector", headerName: "Sector", flex: 2, headerAlign: "left" },
    { field: "deposito", headerName: "Depósito", flex: 1.5, headerAlign: "left" },
    {
      field: "total_contenidos",
      headerName: "Contenidos",
      width: 110,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => <Chip label={p.value} color="primary" size="small" />,
    },
    {
      field: "total_digitalizaciones",
      headerName: "Digitalizaciones",
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => (
        <Chip label={p.value} color={parseInt(p.value) > 0 ? "success" : "default"} size="small" />
      ),
    },
  ];

  const rows = (resultados?.cajas || []).map((c, i) => ({
    id: c.id_caja || i,
    id_caja: c.id_caja,
    referencia: c.referencia,
    cliente: c.cliente,
    sector: c.sector,
    deposito: c.deposito !== "-" ? c.deposito : "Sin depósito",
    total_contenidos: parseInt(c.total_contenidos || 0),
    total_digitalizaciones: parseInt(c.total_digitalizaciones || 0),
  }));

  return (
    <Box sx={{ mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
      {/* Header / toggle */}
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5,
          bgcolor: "primary.main", cursor: "pointer", userSelect: "none",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <BarChartIcon sx={{ color: "#fff" }} />
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1, color: "#fff" }}>
          Estadísticas de Contenidos y Digitalizaciones por Caja
        </Typography>
        {expanded ? <ExpandLessIcon sx={{ color: "#fff" }} /> : <ExpandMoreIcon sx={{ color: "#fff" }} />}
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          {/* Formulario de búsqueda */}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="ID Caja"
                value={idCaja}
                onChange={(e) => setIdCaja(e.target.value.replace(/\D/g, ""))}
                size="small"
                fullWidth
                type="number"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={clientes}
                getOptionLabel={(o) => o.razon_social || ""}
                value={clienteSeleccionado}
                onChange={(_, v) => setClienteSeleccionado(v)}
                size="small"
                renderInput={(params) => <TextField {...params} label="Cliente" />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={sectores}
                getOptionLabel={(o) => o.descripcion || ""}
                value={sectorSeleccionado}
                onChange={(_, v) => setSectorSeleccionado(v)}
                size="small"
                loading={loadingSectores}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sector"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSectores && <CircularProgress size={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                options={depositos}
                getOptionLabel={(o) => o.detalle || ""}
                value={depositoSeleccionado}
                onChange={(_, v) => setDepositoSeleccionado(v)}
                size="small"
                renderInput={(params) => <TextField {...params} label="Depósito" />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2} sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleBuscar}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                size="small"
                fullWidth
              >
                Buscar
              </Button>
              <Button variant="outlined" onClick={handleLimpiar} size="small" fullWidth>
                Limpiar
              </Button>
            </Grid>
          </Grid>

          {/* Cards de totales */}
          {resultados && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                <SummaryCard
                  title="Cajas encontradas"
                  value={totalRecords}
                  icon={<InventoryIcon fontSize="inherit" />}
                  color="#1976d2"
                />
                <SummaryCard
                  title="Total Contenidos"
                  value={resultados.total_contenidos}
                  icon={<FolderIcon fontSize="inherit" />}
                  color="#388e3c"
                />
                <SummaryCard
                  title="Total Digitalizaciones"
                  value={resultados.total_digitalizaciones}
                  icon={<DocumentScannerIcon fontSize="inherit" />}
                  color="#f57c00"
                />
              </Box>

              {/* Tabla con paginación servidor */}
              <StyledDataGrid
                rows={rows}
                columns={columns}
                sx={estiloGrilla}
                loading={loading}
                pagination
                paginationMode="server"
                rowCount={totalRecords}
                pageSize={pageSize}
                disableSelectionOnClick
                density="compact"
                components={{
                  Pagination: () => (
                    <CustomPagination
                      limit={pageSize}
                      handleChangeLimit={handleChangeLimit}
                      page={page}
                      handleFirstPageClick={handleFirstPageClick}
                      totalPages={totalPages}
                      handlePageChange={handlePageChange}
                      totalRowCount={totalRecords}
                      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    />
                  ),
                }}
              />
            </Box>
          )}

          {resultados && totalRecords === 0 && (
            <Typography color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
              No se encontraron datos para los filtros indicados.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
